'use client'

import { useState } from 'react'
import {
  WORKIZ_ONGOING_PLAN,
  WORKIZ_PAID_PLANS,
  WORKIZ_PRICING,
  type WorkizFeature,
  type WorkizPaidPlan,
} from '@/lib/workiz-reporting-data'
import { trackPostHogEvent } from '@/lib/utils/dataLayer'
import { CardCheck } from '@/components/zoho/icons'

function FeatureLine({ label, detail }: WorkizFeature) {
  return (
    <li className="flex items-start gap-2.5">
      <CardCheck />
      <span className="min-w-0 text-[15px] leading-[1.45] text-slate-700">
        <span className="font-semibold text-[#0a2b5c]">{label}</span>
        {detail ? <span className="text-slate-500"> - {detail}</span> : null}
      </span>
    </li>
  )
}

function trackPlan(planId: string, label: string, type: 'primary' | 'secondary') {
  trackPostHogEvent('cta_click', {
    cta_text: label,
    cta_location: 'workiz_plans',
    cta_type: type,
    plan: planId,
  })
}

function PlanCard({
  plan,
  loading,
  error,
  onCheckout,
}: {
  plan: WorkizPaidPlan
  loading: boolean
  error: string | null
  onCheckout: (plan: WorkizPaidPlan) => void
}) {
  return (
    <article
      className={`relative flex flex-col overflow-hidden rounded-[28px] bg-white shadow-[0_24px_80px_rgba(15,35,70,0.12)] ${
        plan.highlight
          ? 'ring-2 ring-[#13479c] ring-offset-4 ring-offset-[#eef4fc]'
          : 'border border-slate-200'
      }`}
    >
      {plan.badge && (
        <div className="absolute right-6 top-6 rounded-full bg-[#13479c] px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.12em] text-white">
          {plan.badge}
        </div>
      )}
      <div className="flex flex-1 flex-col px-7 pb-7 pt-8 sm:px-8">
        <h3 className="pr-28 text-[24px] font-black tracking-[-0.04em] text-[#0a2b5c] sm:text-[28px]">
          {plan.name}
        </h3>
        <p className="mt-2 text-[16px] font-semibold leading-[1.4] text-[#13479c]">{plan.tagline}</p>

        <div className="mt-6">
          <span className="text-[44px] font-black leading-none tracking-[-0.04em] text-[#0a2b5c]">
            {plan.priceDisplay}
          </span>
          <p className="mt-2 text-[13.5px] leading-[1.45] text-slate-500">{plan.priceNote}</p>
        </div>

        <p className="mt-5 text-[14.5px] leading-[1.55] text-slate-600">{plan.bestFor}</p>

        <ul className="mt-7 space-y-3.5">
          {plan.features.map((feature) => (
            <FeatureLine key={feature.label} {...feature} />
          ))}
        </ul>

        <p className="mt-6 text-[13px] font-semibold text-[#0a2b5c]">Turnaround: {plan.turnaround}</p>

        <div className="mt-auto pt-8">
          <button
            type="button"
            disabled={loading}
            className={`inline-flex h-12 w-full items-center justify-center rounded-full px-7 text-[15px] font-bold transition-colors disabled:opacity-60 ${
              plan.highlight
                ? 'bg-[#13479c] text-white shadow-[0_10px_24px_rgba(19,71,156,0.24)] hover:bg-[#0f3a7a]'
                : 'border border-[#13479c] bg-white text-[#13479c] hover:bg-blue-50'
            }`}
            onClick={() => onCheckout(plan)}
          >
            {loading ? 'Redirecting…' : plan.cta}
          </button>
          {error && <p className="mt-3 text-center text-[13px] text-red-600">{error}</p>}
        </div>
      </div>
    </article>
  )
}

export default function WorkizPlans() {
  const [loadingId, setLoadingId] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})

  async function startCheckout(plan: WorkizPaidPlan) {
    if (loadingId) return
    setErrors((prev) => ({ ...prev, [plan.id]: '' }))
    setLoadingId(plan.id)
    trackPlan(plan.id, plan.cta, plan.highlight ? 'primary' : 'secondary')

    try {
      const res = await fetch('/api/services/book', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product: plan.productId }),
      })
      const data = await res.json()

      if (!res.ok || !data.url) {
        setErrors((prev) => ({
          ...prev,
          [plan.id]: data.error || 'Could not start checkout. Please try again.',
        }))
        setLoadingId(null)
        return
      }

      if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          event: 'ph_checkout_started',
          serviceName: plan.name,
          serviceSlug: plan.slug,
          totalPrice: plan.price,
        })
      }

      window.location.href = data.url
    } catch {
      setErrors((prev) => ({ ...prev, [plan.id]: 'Network error. Please try again.' }))
      setLoadingId(null)
    }
  }

  return (
    <section className="bg-[#eef4fc] px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
      <div id="plans" className="mx-auto max-w-6xl scroll-mt-0">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-[#13479c]">
            {WORKIZ_PRICING.title}
          </p>
          <h2 className="mt-3 text-[32px] font-extrabold leading-[1.15] tracking-[-0.035em] text-[#0a2b5c] sm:text-[42px]">
            {WORKIZ_PRICING.subtitle}
          </h2>
        </div>

        <div className="mt-12 grid items-stretch gap-6 lg:grid-cols-2 lg:gap-8">
          {WORKIZ_PAID_PLANS.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              loading={loadingId === plan.id}
              error={errors[plan.id] || null}
              onCheckout={startCheckout}
            />
          ))}
        </div>

        <article className="mt-6 overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_24px_80px_rgba(15,35,70,0.08)]">
          <div className="px-7 py-8 sm:px-10 sm:py-10">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between lg:gap-10">
              <div className="min-w-0 flex-1">
                <h3 className="text-[22px] font-black tracking-[-0.04em] text-[#0a2b5c] sm:text-[26px]">
                  {WORKIZ_ONGOING_PLAN.name}
                </h3>
                <p className="mt-2 text-[16px] font-semibold leading-[1.4] text-[#13479c]">
                  {WORKIZ_ONGOING_PLAN.tagline}
                </p>
                <p className="mt-3 text-[15px] leading-[1.55] text-slate-600">{WORKIZ_ONGOING_PLAN.bestFor}</p>
              </div>
              <div className="shrink-0 lg:w-56 lg:text-right">
                <span className="text-[32px] font-black leading-none tracking-[-0.04em] text-[#0a2b5c]">
                  {WORKIZ_ONGOING_PLAN.priceDisplay}
                </span>
                <p className="mt-2 text-[13.5px] leading-[1.45] text-slate-500">{WORKIZ_ONGOING_PLAN.priceNote}</p>
              </div>
            </div>

            <ul className="mt-7 grid gap-3 sm:grid-cols-2">
              {WORKIZ_ONGOING_PLAN.features.map((feature) => (
                <FeatureLine key={feature.label} {...feature} />
              ))}
            </ul>

            <div className="mt-8 flex justify-center">
              <a
                href="#get-quote"
                className="inline-flex h-12 items-center justify-center rounded-full border border-[#13479c] bg-white px-8 text-[15px] font-bold text-[#13479c] transition-colors hover:bg-blue-50"
                onClick={() => trackPlan(WORKIZ_ONGOING_PLAN.id, WORKIZ_ONGOING_PLAN.cta, 'secondary')}
              >
                {WORKIZ_ONGOING_PLAN.cta}
              </a>
            </div>
          </div>
        </article>
      </div>
    </section>
  )
}
